import React from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  formatCurrencyPriceProduct,
  getPaymentConditionValues,
} from "@utils/index";
import { IProduct } from "@utils/interfaces";

import { useAppointmentPriceContext } from "../context/AppointmentPriceContext";
import { useProductPrice } from "../hooks/useProductPrice";
import PageNotFound from "@components/PageNotFound/PageNotFound";
import { CactusIcon, ShoppingCartOrder } from "@components/Icons";

const textSx = { fontFamily: "Montserrat", color: "#3E3D3D" } as const;
const labelSx = {
  ...textSx,
  fontSize: 12,
  fontWeight: 700,
  color: "#726551",
} as const;
const valueSx = { ...textSx, fontSize: 14 } as const;
const cellSx = {
  display: "flex",
  alignItems: "center",
  ...textSx,
  fontSize: 16,
} as const;
const headerCellSx = { ...cellSx, fontWeight: 700 } as const;

const paperSx = {
  borderRadius: 2,
  px: { xs: 2, sm: 4 },
  py: { xs: 1, sm: 0 },
  boxShadow: "0px 4px 4px rgba(115, 97, 97, 0.10)",
} as const;

const selectSx = {
  fontFamily: "Montserrat",
  fontSize: { xs: 14, sm: 16 },
  borderRadius: 2,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(114, 101, 81, 0.20)",
  },
  "& .MuiSvgIcon-root": { fill: "#D91F05" },
} as const;

const PaymentSelect: React.FC<{
  value: number;
  options: { value: number; label: string }[];
  disabled: boolean;
  loading: boolean;
  onChange: (value: number) => void;
}> = ({ value, options, disabled, loading, onChange }) => (
  <FormControl fullWidth disabled={disabled} variant="outlined" size="small">
    <Select
      sx={selectSx}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      MenuProps={{ anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
      endAdornment={
        loading ? (
          <InputAdornment position="end">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : undefined
      }
      IconComponent={ExpandMoreIcon}
    >
      {options.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/** Single component, single hook call, two CSS-driven layouts */
const ProductItem: React.FC<{ product: IProduct }> = ({ product }) => {
  const { dataCustomer } = useAppointmentPriceContext();
  const {
    loadingPrice,
    loadingCustomerData,
    price,
    displayPrice,
    setDeadlinePayment,
  } = useProductPrice(product);

  const paymentOptions = getPaymentConditionValues(
    (dataCustomer?.products.find((p) => p.id === product.id)
      ?.payCond as number) ?? 0
  );

  const priceContent = loadingPrice ? (
    <CircularProgress size={20} />
  ) : (
    formatCurrencyPriceProduct(displayPrice)
  );

  const selectContent = (
    <PaymentSelect
      value={price ? Number(price.payCond) : 0}
      options={paymentOptions}
      disabled={loadingPrice}
      loading={loadingCustomerData}
      onChange={setDeadlinePayment}
    />
  );

  return (
    <>
      {/* Desktop row */}
      <Paper sx={{ ...paperSx, display: { xs: "none", sm: "block" } }}>
        <Grid container sx={{ height: 90, my: 1, alignItems: "center" }}>
          <Grid item sm={1} sx={cellSx}>
            {product.id}
          </Grid>
          <Grid item sm={6} sx={cellSx}>
            {product.description}
          </Grid>
          <Grid item sm={3} sx={{ ...cellSx, justifyContent: "center" }}>
            {priceContent}
          </Grid>
          <Grid item sm={2} sx={cellSx}>
            {selectContent}
          </Grid>
        </Grid>
      </Paper>

      {/* Mobile card */}
      <Paper sx={{ ...paperSx, display: { xs: "block", sm: "none" }, mb: 1.5 }}>
        <Stack spacing={1.5} sx={{ py: 2 }}>
          <Box>
            <Typography sx={labelSx}>Produto</Typography>
            <Typography sx={valueSx}>
              {product.id} - {product.description}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={labelSx}>Preço</Typography>
              <Typography sx={{ ...valueSx, fontWeight: 700 }}>
                {priceContent}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography sx={labelSx}>Prazo</Typography>
              {selectContent}
            </Box>
          </Box>
        </Stack>
      </Paper>
    </>
  );
};

const TablePrices: React.FC = () => {
  const { customerStatus, dataCustomer } = useAppointmentPriceContext();

  if (customerStatus === "idle" || customerStatus === "loading") {
    return (
      <PageNotFound
        title="Não há nada para mostrar"
        subtitle="Faça uma busca através dos filtros para consultar preços de produtos"
        icon={<ShoppingCartOrder />}
      />
    );
  }

  if (dataCustomer?.hiddePrice === false) {
    return (
      <PageNotFound
        title="Não houve resultado para o filtro da pesquisa."
        subtitle="Por favor tente outro filtro."
        icon={<CactusIcon />}
      />
    );
  }

  return (
    <Collapse in={customerStatus === "loaded"}>
      <Typography sx={{ ...textSx, textAlign: "center", fontSize: 16, mb: 2 }}>
        Resultados da pesquisa
      </Typography>

      {/* Desktop header — hidden on mobile */}
      <Paper sx={{ ...paperSx, display: { xs: "none", sm: "block" } }}>
        <Grid container sx={{ height: 60, alignItems: "center" }}>
          <Grid item sm={1} sx={headerCellSx}>
            ID
          </Grid>
          <Grid item sm={6} sx={headerCellSx}>
            Produto
          </Grid>
          <Grid item sm={3} sx={{ ...headerCellSx, justifyContent: "center" }}>
            Preço
          </Grid>
          <Grid item sm={2} sx={{ ...headerCellSx, justifyContent: "center" }}>
            Prazo
          </Grid>
        </Grid>
      </Paper>

      {dataCustomer?.products.map((product) => (
        <ProductItem product={product} key={product.id} />
      ))}
    </Collapse>
  );
};

export { TablePrices };
